import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";

import { createReference } from "@medplum/core";
import { Practitioner } from "@medplum/fhirtypes";
import { useMedplum, useMedplumProfile } from "@medplum/react";

import { Button, Card, Group, Textarea } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { showNotification } from "@mantine/notifications";

import {
  ClientNote,
  ClientNoteForm,
  ClientNoteStatus,
  ClientNoteTypeForm,
  saveClientClinicalImpression,
  saveClientNoteForm,
} from "@/clients/zod-schemas/client-note";
import { TextWrapper } from "@/components/TextWrapper";

export default function AddClientReminderNote({
  handleToggleReminder,
}: {
  handleToggleReminder: () => void;
}) {
  const { clientId } = useParams() as { clientId: string };
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner;
  const [formTopLevelError, setFormTopLevelError] = useState<string | null>(null);
  const form = useForm<ClientNoteTypeForm>({
    initialValues: { noteStatus: ClientNoteStatus.InProgress, noteText: "" },
    validate: zodResolver(ClientNoteForm),
  });

  const handleAddNoteSubmit = useCallback(
    async (formValues: ClientNoteTypeForm) => {
      try {
        const questionnaireResponse = await saveClientNoteForm({
          medplum,
          form: formValues,
          clientId,
          practitionerReference: createReference(profile),
          type: ClientNote.Reminder,
        });
        await saveClientClinicalImpression({ medplum, questionnaireResponse });
        handleToggleReminder();
      } catch (err) {
        showNotification({
          color: "red",
          message: "Cannot save empty note",
          autoClose: false,
          styles: { description: { whiteSpace: "pre-line" } },
        });
      }
    },
    [medplum, clientId, profile, handleToggleReminder],
  );

  return (
    <Card>
      <form
        onSubmit={form.onSubmit(
          (remindersNote) => {
            handleAddNoteSubmit(remindersNote);
          },
          (validationErrors) => {
            if (Object.keys(validationErrors).length === 1 && validationErrors?.[""]) {
              setFormTopLevelError(validationErrors[""] as string);
            }
          },
        )}
      >
        {formTopLevelError ? (
          <Group>
            <TextWrapper size="sm" c="var(--mantine-color-error)">
              {formTopLevelError}
            </TextWrapper>
          </Group>
        ) : null}
        <Textarea
          autosize
          variant="unstyled"
          placeholder="Type note here"
          {...form.getInputProps("noteText")}
          data-testid="add-reminder-note"
        />
        <Group pt="xs" justify="flex-end">
          <Button color="black" variant="subtle" size="compact-xs" onClick={handleToggleReminder}>
            Cancel
          </Button>
          <Button color="black" type="submit" variant="outline" size="compact-xs">
            Save
          </Button>
        </Group>
      </form>
    </Card>
  );
}
